import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { banners } from "@/mocks/banners";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index: number) => {
    setActive((index + banners.length) % banners.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const current = banners[active];

  return (
    <section
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[360px] w-full sm:h-[440px] md:h-[560px]">
        {banners.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.image}
              alt={`${item.title} 焦点推荐海报`}
              title={`${item.title} - 国产自拍焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/45 to-transparent" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto w-full px-4 md:px-6">
            <div className="max-w-xl">
              <h1 className="font-heading text-sm font-bold tracking-[0.35em] text-accent-300 md:text-base">
                国产自拍 · 国产自制影视内容平台
              </h1>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50">
                <i className="ri-fire-fill" />
                {current.tag}
              </span>
              <h2 className="mt-4 font-heading text-3xl font-black tracking-wide text-background-50 md:text-5xl">
                {current.title}
              </h2>
              <p className="mt-3 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-100 md:text-base">
                {current.subtitle}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/detail/${current.workId}`)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看详情
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/category/movie")}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-compass-3-line text-lg" />
                  发现更多
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(active - 1)}
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-accent-500 md:flex"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(active + 1)}
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-accent-500 md:flex"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {banners.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === active
                  ? "w-7 bg-accent-500"
                  : "w-2 bg-background-50/60 hover:bg-background-50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}